"use client";

import { useTranslations } from "next-intl";
import { Link } from "@/navigation";

export default function MachTable() {
	const t = useTranslations("cncDesignGuidelines");

	return (
		<section className="bg-white py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				{/* 标题区域 */}
				<div className="mx-auto mb-6 sm:mb-8 md:mb-10 lg:mb-12 text-center">
					<h2 className="mb-3 sm:mb-4 text-2xl font-bold leading-tight text-black sm:text-[30px] md:text-4xl">
						{t("title")}
					</h2>
					<p className="text-sm leading-relaxed md:text-base text-black">
						{t("description")}
					</p>
				</div>

				{/* 表格区域 - 移动端可横向滚动 */}
				<div className="mx-auto overflow-x-auto rounded-2xl bg-white shadow-lg">
					<div className="min-w-[800px]">
						{/* 表头 */}
						<div className="grid grid-cols-[1fr_2fr_1.5fr]">
							<div className="bg-[#107e03] px-6 py-4 text-center text-lg font-bold text-white">
								{t("headerGuideline")}
							</div>
							<div className="bg-[#64a525] px-6 py-4 text-center text-lg font-bold text-white">
								{t("headerDescription")}
							</div>
							<div className="bg-[#a7d17d] px-6 py-4 text-center text-lg font-bold text-white">
								{t("headerNotes")}
							</div>
						</div>

						{/* 表格内容 */}
						<div className="divide-y divide-gray-100">
							{/* Radii 行 */}
							<div className="grid grid-cols-[1fr_2fr_1.5fr] items-center">
								<div className="flex h-full items-center border-r border-gray-100 px-6 py-12 max-md:py-6">
									<span className="text-lg font-bold text-black">
										{t("radii")}
									</span>
								</div>
								<div className="flex h-full flex-col justify-center border-r border-gray-100 bg-[#ededed] px-6 py-12 max-md:py-6">
									<p className="mb-2 text-base font-semibold text-black">
										{t("radiiDesc")}
									</p>
									<div className="flex items-center justify-between">
										<span className="text-sm text-gray-900">
											{t("radiiDrillDepth")}
										</span>
										<span className="rounded bg-white px-3 py-1 text-sm font-medium text-mainColor">
											{t("radiiDrillDepthValue")}
										</span>
									</div>
								</div>
								<div className="flex h-full items-center bg-gray-50 px-6 py-12 max-md:py-6">
									<p className="text-base text-gray-900">{t("radiiNotes")}</p>
								</div>
							</div>

							{/* Threads and Tapped Holes 行 */}
							<div className="grid grid-cols-[1fr_2fr_1.5fr] items-center">
								<div className="flex h-full items-center border-r border-gray-100 px-6 py-12 max-md:py-6">
									<span className="text-lg font-bold text-black">
										{t("threads")}
									</span>
								</div>
								<div className="flex h-full flex-col justify-center border-r border-gray-100 bg-[#ededed] px-6 py-12 max-md:py-6">
									<p className="mb-3 text-base font-semibold text-black">
										{t("threadsDesc")}
									</p>
									<div className="space-y-2">
										<div className="flex items-center justify-between">
											<span className="text-sm text-gray-900">
												{t("threadsValue1")}
											</span>
											<span className="rounded bg-white px-3 py-1 text-sm font-medium text-mainColor">
												{t("threadsValue1Depth")}
											</span>
										</div>
										<div className="flex items-center justify-between">
											<span className="text-sm text-gray-900">
												{t("threadsValue2")}
											</span>
											<span className="rounded bg-white px-3 py-1 text-sm font-medium text-mainColor">
												{t("threadsValue2Depth")}
											</span>
										</div>
									</div>
								</div>
								<div className="flex h-full items-center bg-gray-50 px-6 py-12 max-md:py-6">
									<p className="text-base text-gray-900">{t("threadsNotes")}</p>
								</div>
							</div>

							{/* Undercuts 行 */}
							<div className="grid grid-cols-[1fr_2fr_1.5fr] items-center">
								<div className="flex h-full items-center border-r border-gray-100 px-6 py-12 max-md:py-6">
									<span className="text-lg font-bold text-black">
										{t("undercuts")}
									</span>
								</div>
								<div className="flex h-full flex-col justify-center border-r border-gray-100 bg-[#ededed] px-6 py-12 max-md:py-6">
									<p className="mb-3 text-base font-semibold text-black">
										{t("undercutsDesc")}
									</p>
									<div className="flex flex-wrap gap-2">
										<span className="rounded-full border border-themePrimary400 bg-white px-4 py-1.5 text-sm text-themePrimary400">
											{t("undercutsSquare")}
										</span>
										<span className="rounded-full border border-themePrimary400 bg-white px-4 py-1.5 text-sm text-themePrimary400">
											{t("undercutsFullRadius")}
										</span>
										<span className="rounded-full border border-themePrimary400 bg-white px-4 py-1.5 text-sm text-themePrimary400">
											{t("undercutsDovetail")}
										</span>
									</div>
								</div>
								<div className="flex h-full items-center bg-gray-50 px-6 py-12 max-md:py-6">
									<p className="text-base text-gray-900">
										{t("undercutsNotes")}
									</p>
								</div>
							</div>

							{/* Text 行 */}
							<div className="grid grid-cols-[1fr_2fr_1.5fr] items-stretch">
								<div className="flex items-center border-r border-gray-100 px-6 py-12 max-md:py-6">
									<span className="text-lg font-bold text-black">
										{t("text")}
									</span>
								</div>
								<div className="flex flex-col border-r border-gray-100 bg-[#ededed]">
									<div className="border-b border-gray-200 px-6 py-12 max-md:py-6">
										<p className="mb-2 text-base font-semibold text-black">
											{t("textMillingTitle")}
										</p>
										<div className="mb-2 flex items-center justify-between">
											<span className="text-sm text-gray-900">
												{t("textMillingMinWidth")}
											</span>
											<span className="rounded bg-white px-3 py-1 text-sm font-medium text-mainColor">
												{t("textMillingMinWidthValue")}
											</span>
										</div>
										<p className="text-sm text-gray-900">
											{t("textMillingDesc")}
										</p>
									</div>
									<div className="px-6 py-12 max-md:py-6">
										<p className="mb-2 text-base font-semibold text-black">
											{t("textTurningTitle")}
										</p>
										<p className="text-sm text-gray-900">
											{t("textTurningDesc")}
										</p>
									</div>
								</div>
								<div className="flex flex-col bg-gray-50">
									<div className="flex flex-1 items-center border-b border-gray-100 px-6 py-12 max-md:py-6">
										<p className="text-base text-gray-900">
											{t("textMillingNotes")}
										</p>
									</div>
									<div className="flex flex-1 items-center px-6 py-12 max-md:py-6">
										<p className="text-base text-gray-900">
											{t("textTurningNotes")}
										</p>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				{/* 底部 CTA 区域 */}
				<div className="mx-auto mt-12 text-center md:mt-16">
					<h3 className="mb-2 text-sm text-black sm:text-base">
						{t("ctaTitle")}
					</h3>
					<p className="mb-6 text-sm text-black sm:text-base">
						{t("ctaDescription")}
					</p>
					<Link
						href="/about-us"
						className="inline-flex min-w-[240px] items-center justify-center rounded hover:text-white bg-themePrimary400 px-8 py-3.5 text-base font-semibold text-white transition-all duration-300 hover:bg-opacity-90 sm:min-w-[280px]"
					>
						{t("ctaButton")}
					</Link>
				</div>
			</div>
		</section>
	);
}
